// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { Button, CircularProgress } from '@mui/material';
import { brokerAuthenticationApi } from '@advisor-app/api/brokers-authentication/broker-authentication-api';
import { Broker } from '@shared/data-access/interfaces/api/brokerage/account';
import type { BrokerCredentials } from '@shared/data-access/interfaces/api/brokerage/commons';
import { useSafeUpdate } from '@shared/ui-kit/hooks';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { accountApi } from '@advisor-app/store/api/brokerage/accounts-api';
import {
  setBrokerAccount,
  setOpenLinkAccountModal,
} from '@advisor-app/store/slices/broker';
import alpacaLogo from '@advisor-app/assets/img/brokers/alpaca.png';
import ibrkLogo from '@advisor-app/assets/img/brokers/interactive_brokers.png';
import tradierLogo from '@advisor-app/assets/img/brokers/tradier.png';
import { useTranslation } from 'react-i18next';

export const allowedBrokers: Record<Broker, string> = {
  [Broker.Alpaca]: 'Alpaca',
  [Broker.IBKR]: 'Interactive Brokers',
  [Broker.Tradier]: 'Tradier',
};

const brokerLogos: Record<Broker, string> = {
  [Broker.Alpaca]: alpacaLogo,
  [Broker.IBKR]: ibrkLogo,
  [Broker.Tradier]: tradierLogo,
};

export const BrokersList = () => {
  const { t } = useTranslation();
  const runSafeUpdate = useSafeUpdate();

  const dispatch = useDispatch();

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [postBrokerCredentials] = accountApi.usePostBrokerCredentialsMutation();

  const connectToBroker = async (broker: Broker) => {
    const errorMessage = t('accounts.invalid-broker-authentication', {
      ns: 'components',
      broker: allowedBrokers[broker],
    });

    runSafeUpdate(() => {
      setLoading(true);

      setError('');
    });

    try {
      if (process.env['NODE_ENV'] !== 'development') {
        const state = (Math.random() + 1).toString(36).substring(2);

        let authorizationCode: BrokerCredentials = {} as any;

        if (broker === Broker.IBKR) {
          authorizationCode = await brokerAuthenticationApi.interactive_brokers();
        } else {
          authorizationCode = await brokerAuthenticationApi[broker](state);
        }

        const credentials = await postBrokerCredentials({
          brokerKey: broker,
          brokerCredentials: authorizationCode,
        }).unwrap();

        if (!credentials.connected) {
          throw new Error(t('accounts.broker-not-connected', { ns: 'components' }));
        }
        runSafeUpdate(() => {
          dispatch(
            setBrokerAccount({
              broker: broker,
              accountType: credentials.type_account || 'PAPER',
              account: {
                name: '',
                accountId: '',
                currency: '',
              },
            }),
          );

          dispatch(setOpenLinkAccountModal(false));
          setLoading(false);
        });
      } else {
        runSafeUpdate(() => {
          dispatch(
            setBrokerAccount({
              broker: broker,
              accountType: 'PAPER',
              account: {
                name: '',
                accountId: '',
                currency: '',
              },
            }),
          );

          dispatch(setOpenLinkAccountModal(false));
          setLoading(false);
        });
      }
    } catch {
      runSafeUpdate(() => {
        setError(errorMessage);
        setLoading(false);
      });
    }
  };

  return (
    <>
      {loading ? (
        <div className="flex justify-center">
          <CircularProgress />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-2">
          {Object.entries(allowedBrokers).map(([broker, brokerName]) => {
            const brokerLogo = brokerLogos[broker as Broker];
            return (
              <div
                className="grid grid-cols-[32px_1fr] items-center gap-[12px] rounded bg-section p-6"
                key={brokerName}
              >
                <img
                  src={brokerLogo}
                  alt={`${brokerName} ${t('common.logo', { ns: 'common' })}`}
                  className="h-[32px] w-[32px] object-contain"
                />
                <div className="flex items-center justify-between">
                  <p className="text-subtitle">{brokerName}</p>
                  <Button
                    data-testid={`link-${broker}-oauth`}
                    color="secondary"
                    onClick={() => connectToBroker(broker as Broker)}
                  >
                    {t('accounts.link', { ns: 'components' })}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {error && (
        <div className="mt-2 rounded bg-red-100 px-6 py-3 text-red-500">
          {error}
        </div>
      )}
    </>
  );
};
